/**
 * Sentry Utility Functions
 * Centralized utilities for custom error tracking and context management
 */

import * as Sentry from '@sentry/react';

/**
 * Capture a custom error with additional context
 * @param {Error|string} error - Error object or error message
 * @param {Object} context - Additional context to attach to the error
 * @param {string} level - Error level: 'error', 'warning', 'info', 'debug'
 */
export const captureError = (error, context = {}, level = 'error') => {
  const errorObj = typeof error === 'string' ? new Error(error) : error;

  Sentry.captureException(errorObj, {
    level,
    contexts: {
      custom: context,
    },
  });
};

/**
 * Capture a message (non-error event)
 * @param {string} message - Message to capture
 * @param {Object} context - Additional context
 * @param {string} level - Message level: 'error', 'warning', 'info', 'debug'
 */
export const captureMessage = (message, context = {}, level = 'info') => {
  Sentry.captureMessage(message, {
    level,
    contexts: {
      custom: context,
    },
  });
};

/**
 * Add a breadcrumb for tracking user actions
 * @param {string} category - Breadcrumb category (e.g., 'navigation', 'user.action')
 * @param {string} message - Breadcrumb message
 * @param {Object} data - Additional data
 * @param {string} level - Breadcrumb level: 'error', 'warning', 'info', 'debug'
 */
export const addBreadcrumb = (category, message, data = {}, level = 'info') => {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level,
    timestamp: Date.now() / 1000,
  });
};

/**
 * Set custom context for the current scope
 * @param {string} name - Context name
 * @param {Object} context - Context data
 */
export const setCustomContext = (name, context) => {
  Sentry.setContext(name, context);
};

/**
 * Set a tag for filtering in Sentry
 * @param {string} key - Tag key
 * @param {string} value - Tag value
 */
export const setTag = (key, value) => {
  Sentry.setTag(key, value);
};

/**
 * Start a new span for performance monitoring
 * @param {string} name - Span name
 * @param {string} op - Operation name (e.g., 'page.load', 'api.call')
 * @param {Function} callback - Callback to run within the span
 * @returns {*} Result of the callback
 */
export const startTransaction = (name, op = 'custom', callback) => {
  return Sentry.startSpan({ name, op }, callback || (() => {}));
};

/**
 * Track a form submission
 * @param {string} formName - Name of the form
 * @param {Object} formData - Form data (sensitive fields will be filtered)
 */
export const trackFormSubmission = (formName, formData = {}) => {
  // Filter out sensitive fields
  const filteredData = Object.keys(formData).reduce((acc, key) => {
    if (!['password', 'token', 'secret'].some(sensitive => key.toLowerCase().includes(sensitive))) {
      acc[key] = formData[key];
    }
    return acc;
  }, {});

  addBreadcrumb(
    'user.action',
    `Form submitted: ${formName}`,
    { formName, fields: Object.keys(filteredData) },
    'info'
  );
};

/**
 * Track a page view
 * @param {string} pageName - Name of the page
 * @param {Object} pageData - Additional page data
 */
export const trackPageView = (pageName, pageData = {}) => {
  addBreadcrumb(
    'navigation',
    `Page viewed: ${pageName}`,
    { pageName, ...pageData },
    'info'
  );
};

/**
 * Track a user action
 * @param {string} action - Action name
 * @param {Object} actionData - Additional action data
 */
export const trackUserAction = (action, actionData = {}) => {
  addBreadcrumb(
    'user.action',
    action,
    actionData,
    'info'
  );
};

/**
 * Wrap an async function with error tracking
 * @param {Function} fn - Async function to wrap
 * @param {string} functionName - Name of the function for error reporting
 * @returns {Function} Wrapped function
 */
export const withErrorTracking = (fn, functionName) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      captureError(error, {
        function: functionName,
        arguments: args,
      });
      throw error;
    }
  };
};

/**
 * ErrorBoundary fallback component
 */
export const ErrorFallback = ({ error, componentStack, resetError }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '20px',
    textAlign: 'center',
  }}>
    <h2>Oops! Something went wrong</h2>
    <p style={{ maxWidth: '600px', margin: '20px 0', color: '#666' }}>
      We've been notified of this error and will work to fix it as soon as possible.
    </p>
    {error && (
      <details style={{ marginTop: '20px', textAlign: 'left' }}>
        <summary style={{ cursor: 'pointer', marginBottom: '10px' }}>
          Error details
        </summary>
        <pre style={{
          background: '#f5f5f5',
          padding: '10px',
          borderRadius: '4px',
          overflow: 'auto',
          fontSize: '12px',
        }}>
          {error.toString()}
          {componentStack}
        </pre>
      </details>
    )}
    <button
      onClick={resetError}
      style={{
        marginTop: '20px',
        padding: '10px 20px',
        background: '#232562',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '16px',
      }}
    >
      Try again
    </button>
  </div>
);

export default {
  captureError,
  captureMessage,
  addBreadcrumb,
  setCustomContext,
  setTag,
  startTransaction,
  trackFormSubmission,
  trackPageView,
  trackUserAction,
  withErrorTracking,
  ErrorFallback,
};
